import React from 'react';
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  Diagram,
  DiagramElement,
  EquipmentElement,
  LineElement,
  PitchType,
  TEAM_COLORS,
} from './types';

type Point = { x: number; y: number };

// ---------------------------------------------------------------------------
// Geometria
// ---------------------------------------------------------------------------

/** A vonal kontrollpontja: görbénél a tárolt pont, egyenesnél a felezőpont. */
const controlOf = (l: LineElement): Point =>
  l.cx !== undefined && l.cy !== undefined ? { x: l.cx, y: l.cy } : { x: (l.x1 + l.x2) / 2, y: (l.y1 + l.y2) / 2 };

const quadAt = (p0: Point, c: Point, p1: Point, t: number): Point => ({
  x: (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * c.x + t * t * p1.x,
  y: (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * c.y + t * t * p1.y,
});

const quadTangent = (p0: Point, c: Point, p1: Point, t: number): Point => ({
  x: 2 * (1 - t) * (c.x - p0.x) + 2 * t * (p1.x - c.x),
  y: 2 * (1 - t) * (c.y - p0.y) + 2 * t * (p1.y - c.y),
});

/** A görbe "fogópontja" (t = 0.5) - ezt húzza a felhasználó a hajlításhoz. */
export const lineMidHandle = (l: LineElement): Point =>
  quadAt({ x: l.x1, y: l.y1 }, controlOf(l), { x: l.x2, y: l.y2 }, 0.5);

/** A fogópontból visszaszámolt kontrollpont, hogy a görbe átmenjen rajta. */
export const controlFromHandle = (l: LineElement, handle: Point): Point => ({
  x: 2 * handle.x - (l.x1 + l.x2) / 2,
  y: 2 * handle.y - (l.y1 + l.y2) / 2,
});

const ARROW_LENGTH = 18;
const ARROW_HALF_WIDTH = 9;

const linePath = (l: LineElement, withArrow: boolean): { d: string; head?: string } => {
  const p0 = { x: l.x1, y: l.y1 };
  const p1 = { x: l.x2, y: l.y2 };
  const c = controlOf(l);
  const curved = l.cx !== undefined && l.cy !== undefined;

  const tangent = quadTangent(p0, c, p1, 1);
  const tLen = Math.hypot(tangent.x, tangent.y) || 1;
  const dir = { x: tangent.x / tLen, y: tangent.y / tLen };

  let head: string | undefined;
  if (withArrow) {
    const base = { x: p1.x - dir.x * ARROW_LENGTH, y: p1.y - dir.y * ARROW_LENGTH };
    const n = { x: -dir.y, y: dir.x };
    head = [
      `${p1.x},${p1.y}`,
      `${base.x + n.x * ARROW_HALF_WIDTH},${base.y + n.y * ARROW_HALF_WIDTH}`,
      `${base.x - n.x * ARROW_HALF_WIDTH},${base.y - n.y * ARROW_HALF_WIDTH}`,
    ].join(' ');
  }

  if (l.style === 'dribble') {
    // Hullámos vonal a görbe mentén; a végén egyenesen fut be a nyílhegybe
    const approxLength = Math.hypot(c.x - p0.x, c.y - p0.y) + Math.hypot(p1.x - c.x, p1.y - c.y);
    const samples = Math.max(8, Math.round(approxLength / 3));
    const wavelength = 22;
    const amplitude = 6;
    const straightTail = withArrow ? ARROW_LENGTH + 6 : 0;
    const points: string[] = [];
    let travelled = 0;
    let prev = p0;
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const p = quadAt(p0, c, p1, t);
      travelled += Math.hypot(p.x - prev.x, p.y - prev.y);
      prev = p;
      const tg = quadTangent(p0, c, p1, t);
      const len = Math.hypot(tg.x, tg.y) || 1;
      const remaining = approxLength - travelled;
      const fade = remaining < straightTail ? 0 : 1;
      const offset = Math.sin((travelled / wavelength) * Math.PI * 2) * amplitude * fade;
      points.push(`${p.x + (-tg.y / len) * offset},${p.y + (tg.x / len) * offset}`);
    }
    const end = withArrow ? { x: p1.x - dir.x * (ARROW_LENGTH - 2), y: p1.y - dir.y * (ARROW_LENGTH - 2) } : p1;
    points.push(`${end.x},${end.y}`);
    return { d: `M ${points.join(' L ')}`, head };
  }

  // A vonal a nyílhegy tövénél ér véget, hogy ne lógjon ki a hegyből
  const end = withArrow ? { x: p1.x - dir.x * (ARROW_LENGTH - 2), y: p1.y - dir.y * (ARROW_LENGTH - 2) } : p1;

  if (l.style === 'shot') {
    // Lövés: két párhuzamos vonal a görbe mentén, közös nyílheggyel
    const gap = 3.5;
    const samples = curved ? 24 : 1;
    const sides = [gap, -gap].map((offset) => {
      const points: string[] = [];
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const p = quadAt(p0, c, end, t);
        const tg = quadTangent(p0, c, end, t);
        const len = Math.hypot(tg.x, tg.y) || 1;
        points.push(`${p.x + (-tg.y / len) * offset},${p.y + (tg.x / len) * offset}`);
      }
      return `M ${points.join(' L ')}`;
    });
    return { d: sides.join(' '), head };
  }

  const d = curved
    ? `M ${p0.x},${p0.y} Q ${c.x},${c.y} ${end.x},${end.y}`
    : `M ${p0.x},${p0.y} L ${end.x},${end.y}`;
  return { d, head };
};

// ---------------------------------------------------------------------------
// Pálya
// ---------------------------------------------------------------------------

const GRASS_DARK = '#3f8f4d';
const GRASS_LIGHT = '#48a057';

const Grass: React.FC = () => (
  <g>
    <rect x={0} y={0} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill={GRASS_LIGHT} />
    {Array.from({ length: 12 }, (_, i) =>
      i % 2 === 0 ? (
        <rect key={i} x={(CANVAS_WIDTH / 12) * i} y={0} width={CANVAS_WIDTH / 12} height={CANVAS_HEIGHT} fill={GRASS_DARK} />
      ) : null
    )}
  </g>
);

const lineProps = {
  fill: 'none',
  stroke: 'rgba(255,255,255,0.9)',
  strokeWidth: 2.5,
  vectorEffect: 'non-scaling-stroke' as const,
};

/** Teljes pálya méterben (105 x 68), vízszintesen. */
const FullPitchLines: React.FC = () => {
  const s = 9.1;
  const ox = (CANVAS_WIDTH - 105 * s) / 2;
  const oy = (CANVAS_HEIGHT - 68 * s) / 2;
  const box = (left: boolean) => {
    const x = left ? 0 : 105;
    const sign = left ? 1 : -1;
    return (
      <g key={left ? 'l' : 'r'}>
        <rect x={left ? 0 : 105 - 16.5} y={34 - 20.16} width={16.5} height={40.32} {...lineProps} />
        <rect x={left ? 0 : 105 - 5.5} y={34 - 9.16} width={5.5} height={18.32} {...lineProps} />
        <circle cx={x + sign * 11} cy={34} r={0.3} fill="white" />
        {/* A tizenhatos íve: a büntetőpont körüli 9,15 m-es kör tizenhatoson kívüli része */}
        <path
          d={`M ${x + sign * 16.5} ${34 - 7.31} A 9.15 9.15 0 0 ${left ? 1 : 0} ${x + sign * 16.5} ${34 + 7.31}`}
          {...lineProps}
        />
        <rect x={left ? -2 : 105} y={34 - 3.66} width={2} height={7.32} {...lineProps} />
      </g>
    );
  };
  return (
    <g transform={`translate(${ox} ${oy}) scale(${s})`}>
      <rect x={0} y={0} width={105} height={68} {...lineProps} />
      <line x1={52.5} y1={0} x2={52.5} y2={68} {...lineProps} />
      <circle cx={52.5} cy={34} r={9.15} {...lineProps} />
      <circle cx={52.5} cy={34} r={0.3} fill="white" />
      {box(true)}
      {box(false)}
    </g>
  );
};

/** Fél pálya álló helyzetben, a kapu felül (68 széles, 52,5 mély). */
const HalfPitchLines: React.FC = () => {
  const s = 11.4;
  const ox = (CANVAS_WIDTH - 68 * s) / 2;
  const oy = (CANVAS_HEIGHT - 52.5 * s) / 2 + 6;
  return (
    <g transform={`translate(${ox} ${oy}) scale(${s})`}>
      <rect x={0} y={0} width={68} height={52.5} {...lineProps} />
      <rect x={34 - 20.16} y={0} width={40.32} height={16.5} {...lineProps} />
      <rect x={34 - 9.16} y={0} width={18.32} height={5.5} {...lineProps} />
      <circle cx={34} cy={11} r={0.3} fill="white" />
      <path d={`M ${34 - 7.31} 16.5 A 9.15 9.15 0 0 0 ${34 + 7.31} 16.5`} {...lineProps} />
      <rect x={34 - 3.66} y={-2} width={7.32} height={2} {...lineProps} />
      <path d={`M ${34 - 9.15} 52.5 A 9.15 9.15 0 0 1 ${34 + 9.15} 52.5`} {...lineProps} />
      <circle cx={34} cy={52.5} r={0.3} fill="white" />
    </g>
  );
};

export const Pitch: React.FC<{ type: PitchType }> = ({ type }) => (
  <g pointerEvents="none">
    <Grass />
    {type === 'full' && <FullPitchLines />}
    {type === 'half' && <HalfPitchLines />}
  </g>
);

// ---------------------------------------------------------------------------
// Elemek
// ---------------------------------------------------------------------------

const Equipment: React.FC<{ el: EquipmentElement }> = ({ el }) => {
  const transform = `translate(${el.x} ${el.y}) rotate(${el.rotation || 0})`;
  switch (el.kind) {
    case 'ball':
      return (
        <g transform={transform}>
          <circle r={9} fill="#ffffff" stroke="#111827" strokeWidth={1.5} />
          <path d="M 0 -3.5 L 3.3 -1.1 L 2 2.8 L -2 2.8 L -3.3 -1.1 Z" fill="#111827" />
        </g>
      );
    case 'cone':
      return (
        <g transform={transform}>
          <path d="M 0 -13 L 10 9 L -10 9 Z" fill={el.color} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />
          <rect x={-12} y={8} width={24} height={4} rx={1.5} fill={el.color} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />
        </g>
      );
    case 'disc':
      return (
        <g transform={transform}>
          <ellipse rx={11} ry={6} fill={el.color} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />
          <ellipse rx={3.5} ry={2} fill="rgba(0,0,0,0.25)" />
        </g>
      );
    case 'pole':
      return (
        <g transform={transform}>
          <ellipse cy={14} rx={7} ry={3} fill="rgba(0,0,0,0.3)" />
          <rect x={-2.5} y={-20} width={5} height={34} rx={2} fill={el.color} stroke="rgba(0,0,0,0.35)" strokeWidth={1} />
        </g>
      );
    case 'minigoal':
    case 'goal': {
      const w = el.kind === 'goal' ? 76 : 40;
      const d = el.kind === 'goal' ? 20 : 14;
      return (
        <g transform={transform}>
          <rect x={-w / 2} y={-d / 2} width={w} height={d} fill="rgba(255,255,255,0.18)" stroke="#ffffff" strokeWidth={3} />
          {Array.from({ length: Math.floor(w / 8) - 1 }, (_, i) => (
            <line key={i} x1={-w / 2 + (i + 1) * 8} y1={-d / 2} x2={-w / 2 + (i + 1) * 8} y2={d / 2} stroke="rgba(255,255,255,0.55)" strokeWidth={1} />
          ))}
          <line x1={-w / 2} y1={0} x2={w / 2} y2={0} stroke="rgba(255,255,255,0.55)" strokeWidth={1} />
        </g>
      );
    }
    default:
      return null;
  }
};

const ElementShape: React.FC<{ el: DiagramElement }> = ({ el }) => {
  switch (el.type) {
    case 'player': {
      const color = TEAM_COLORS[el.color] ?? TEAM_COLORS.red;
      return (
        <g transform={`translate(${el.x} ${el.y})`}>
          <circle r={17} fill="rgba(0,0,0,0.25)" cy={2} />
          <circle r={16} fill={color.fill} stroke={el.color === 'white' ? '#1f2937' : '#ffffff'} strokeWidth={2.5} />
          {el.label && (
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={el.label.length > 2 ? 11 : 15}
              fontWeight={700}
              fill={color.text}
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {el.label}
            </text>
          )}
        </g>
      );
    }
    case 'equipment':
      return <Equipment el={el} />;
    case 'line': {
      const withArrow = el.style !== 'line';
      const { d, head } = linePath(el, withArrow);
      return (
        <g>
          <path
            d={d}
            fill="none"
            stroke={el.color}
            strokeWidth={el.style === 'shot' ? 2.5 : 3.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={el.style === 'run' ? '12 9' : undefined}
          />
          {head && <polygon points={head} fill={el.color} />}
        </g>
      );
    }
    case 'zone': {
      const common = {
        fill: el.color,
        fillOpacity: 0.22,
        stroke: el.color,
        strokeWidth: 2.5,
        strokeDasharray: '10 6',
      };
      return el.shape === 'ellipse' ? (
        <ellipse cx={el.x + el.w / 2} cy={el.y + el.h / 2} rx={el.w / 2} ry={el.h / 2} {...common} />
      ) : (
        <rect x={el.x} y={el.y} width={el.w} height={el.h} rx={6} {...common} />
      );
    }
    case 'text':
      return (
        <text
          x={el.x}
          y={el.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={el.size}
          fontWeight={700}
          fill={el.color}
          stroke={el.color === '#ffffff' || el.color === '#facc15' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.8)'}
          strokeWidth={3}
          paintOrder="stroke"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {el.text}
        </text>
      );
    default:
      return null;
  }
};

/** Láthatatlan, vastagabb "fogófelület" a vonalakhoz, hogy könnyű legyen rákattintani. */
const HitArea: React.FC<{ el: DiagramElement }> = ({ el }) => {
  if (el.type !== 'line') return null;
  const { d } = linePath({ ...el, style: el.style === 'dribble' || el.style === 'shot' ? 'pass' : el.style }, false);
  return <path d={d} fill="none" stroke="transparent" strokeWidth={22} strokeLinecap="round" />;
};

interface DiagramCanvasProps {
  diagram: Diagram;
  className?: string;
  svgRef?: React.Ref<SVGSVGElement>;
  onElementPointerDown?: (e: React.PointerEvent, id: string) => void;
  onBackgroundPointerDown?: (e: React.PointerEvent) => void;
  onPointerMove?: (e: React.PointerEvent) => void;
  onPointerUp?: (e: React.PointerEvent) => void;
  /** A kijelölés kerete és fogópontjai - a szerkesztő rajzolja rá. */
  overlay?: React.ReactNode;
  cursor?: string;
  title?: string;
}

/**
 * Az ábra kirajzolása. Csak megjelenít - az interakciót a szerkesztő köti rá a
 * callbackeken keresztül, így ugyanez adja az előnézetet és a szerkesztőt is.
 */
const DiagramCanvas: React.FC<DiagramCanvasProps> = ({
  diagram,
  className,
  svgRef,
  onElementPointerDown,
  onBackgroundPointerDown,
  onPointerMove,
  onPointerUp,
  overlay,
  cursor,
  title,
}) => {
  // A zónák alul, a szöveg és a játékosok felül legyenek
  const order: Record<DiagramElement['type'], number> = { zone: 0, line: 1, equipment: 2, player: 3, text: 4 };
  const elements = [...diagram.elements].sort((a, b) => order[a.type] - order[b.type]);

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
      className={className}
      style={{ touchAction: onElementPointerDown ? 'none' : undefined, cursor }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onLostPointerCapture={onPointerUp}
      role="img"
      aria-label={title ?? 'Edzés ábra'}
    >
      <g onPointerDown={onBackgroundPointerDown}>
        <Pitch type={diagram.pitch} />
        <rect x={0} y={0} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill="transparent" />
      </g>
      {elements.map((el) => (
        <g
          key={el.id}
          onPointerDown={onElementPointerDown ? (e) => onElementPointerDown(e, el.id) : undefined}
          style={onElementPointerDown ? { cursor: 'move' } : undefined}
        >
          <HitArea el={el} />
          <ElementShape el={el} />
        </g>
      ))}
      {overlay}
    </svg>
  );
};

export default DiagramCanvas;
